import {
  CHAT_FONT_CATEGORY_ORDER,
  CHAT_FONT_FAMILIES,
  CHAT_FONT_KEYS,
  ChatFontCategory,
  ChatFontKey
} from 'lib/chatFontCatalog'

export type { ChatFontKey }

export type CatalogChatFontKey = Exclude<ChatFontKey, 'DEFAULT'>

const LEGACY_FONT_REMAP: Record<string, ChatFontKey> = {
  AVENIR: 'INTER',
  AVENIR_CONDENSED: 'INTER'
}

export const isChatFontKey = (v: string): v is ChatFontKey =>
  (CHAT_FONT_KEYS as readonly string[]).includes(v)

export const normalizeChatFontKey = (v: string): ChatFontKey => {
  const remapped = LEGACY_FONT_REMAP[v] ?? v
  return isChatFontKey(remapped) ? remapped : 'DEFAULT'
}

const CATEGORY_FALLBACK: Record<ChatFontCategory, string> = {
  sans: 'sans-serif',
  rounded: 'sans-serif',
  display: 'sans-serif',
  tech: 'sans-serif',
  handwritten: 'cursive',
  serif: 'serif',
  mono: 'monospace'
}

export const catalogFontStack = (key: CatalogChatFontKey): string => {
  const { family, category } = CHAT_FONT_FAMILIES[key]
  return `"${family}", ${CATEGORY_FALLBACK[category]}`
}

export const chatFontFamily = (key: string): string | undefined => {
  const normalized = normalizeChatFontKey(key)
  if (normalized === 'DEFAULT') return undefined
  return catalogFontStack(normalized)
}

export const CHAT_MESSAGE_WEIGHT_DEFAULT = 400
export const CHAT_USERNAME_WEIGHT_DEFAULT = 700
export const CHAT_FONT_WEIGHT_MIN = 100
export const CHAT_FONT_WEIGHT_MAX = 1000

export const cssMatchedCut = (cuts: readonly number[], desired: number): number => {
  if (cuts.includes(desired)) return desired
  const below = [...cuts].filter((c) => c < desired).sort((a, b) => b - a)
  const above = [...cuts].filter((c) => c > desired).sort((a, b) => a - b)
  if (desired < 400) return below[0] ?? above[0]
  if (desired > 500) return above[0] ?? below[0]
  const inBand = above.filter((c) => c <= 500)
  return inBand[0] ?? below[0] ?? above[0]
}

export const preRangePinnedWeight = (key: string, weight: number): number => {
  const normalized = normalizeChatFontKey(key)
  if (normalized === 'DEFAULT') return weight
  const info = CHAT_FONT_FAMILIES[normalized]
  if (info.wght === undefined) return weight
  return cssMatchedCut(info.weights, weight)
}

export type ChatFontWeightCapability =
  | { kind: 'range', min: number, max: number }
  | { kind: 'cuts', cuts: readonly number[] }
  | { kind: 'fixed' }

export const chatFontWeightCapability = (key: string): ChatFontWeightCapability => {
  const normalized = normalizeChatFontKey(key)
  if (normalized === 'DEFAULT') return { kind: 'range', min: 100, max: 900 }
  const info = CHAT_FONT_FAMILIES[normalized]
  if (info.wght !== undefined) return { kind: 'range', min: info.wght[0], max: info.wght[1] }
  if (info.weights.length === 1) return { kind: 'fixed' }
  return { kind: 'cuts', cuts: info.weights }
}

export const effectiveChatFontWeight = (
  key: string,
  stored: unknown,
  roleDefault: number
): number => {
  const raw = typeof stored === 'number' && Number.isFinite(stored)
    ? Math.round(stored)
    : roleDefault
  const clamped = Math.min(CHAT_FONT_WEIGHT_MAX, Math.max(CHAT_FONT_WEIGHT_MIN, raw))
  const capability = chatFontWeightCapability(key)
  if (capability.kind === 'fixed') return roleDefault
  if (capability.kind === 'cuts') return cssMatchedCut(capability.cuts, clamped)
  return Math.min(capability.max, Math.max(capability.min, clamped))
}

export interface ChatWeightChoices {
  kind: 'variable' | 'cuts' | 'fixed'
  presets: number[]
  custom: { min: number, max: number } | null
}

export const chatWeightChoices = (key: string): ChatWeightChoices => {
  const capability = chatFontWeightCapability(key)
  if (capability.kind === 'range') {
    const { min, max } = capability
    const grid: number[] = []
    for (let w = Math.ceil(min / 100) * 100; w <= max; w += 100) grid.push(w)
    const presets = [...new Set([min, ...grid, max])].sort((a, b) => a - b)
    return { kind: 'variable', presets, custom: { min, max } }
  }
  if (capability.kind === 'cuts') {
    return { kind: 'cuts', presets: [...capability.cuts].sort((a, b) => a - b), custom: null }
  }
  const normalized = normalizeChatFontKey(key)
  const weights = normalized === 'DEFAULT' ? [] : CHAT_FONT_FAMILIES[normalized].weights
  return { kind: 'fixed', presets: [...weights].sort((a, b) => a - b), custom: null }
}

export const DEFAULT_CHAT_FONT_OPTION = {
  key: 'DEFAULT' as const,
  label: 'Default (system font)'
}

const CATEGORY_LABEL: Record<ChatFontCategory, string> = {
  sans: 'Sans serif',
  rounded: 'Rounded',
  display: 'Display',
  tech: 'Tech & pixel',
  handwritten: 'Handwritten',
  serif: 'Serif',
  mono: 'Monospace'
}

export interface ChatFontOptionGroup {
  category: ChatFontCategory
  label: string
  options: Array<{ key: ChatFontKey, label: string }>
}

export const CHAT_FONT_OPTION_GROUPS: readonly ChatFontOptionGroup[] =
  CHAT_FONT_CATEGORY_ORDER.map((category) => ({
    category,
    label: CATEGORY_LABEL[category],
    options: (Object.keys(CHAT_FONT_FAMILIES) as CatalogChatFontKey[])
      .filter((key) => CHAT_FONT_FAMILIES[key].category === category)
      .map((key) => ({ key, label: CHAT_FONT_FAMILIES[key].family }))
  }))
